import React from "react";
import { Button } from "../ui/button";
import { Plus, Loader2, PenLine, UserRound, AlertTriangle } from "lucide-react";
import RepresentativeFormDialog from "../RepresentativeFormDialog";
import { MAX_SIGNERS, maskCPF, signerMissingFields } from "../../lib/representatives";
import { REPRESENTANTE_INCOMPLETO_COPY } from "./config";

// R5 — extraído MECANICAMENTE de pages/NewOperation.jsx (JSX verbatim; o
// estado vive no componente-pai e chega por `ctx`).
export default function StepAssinantes({ ctx }) {
  const {
    cfg,
    cedenteId,
    signers,
    signersLoading,
    signersError,
    selectedSignerIds,
    repDialog,
    setRepDialog,
    repBasePath,
    loadSigners,
    selectedSigners,
    incompleteSelected,
    toggleSigner,
  } = ctx;
  return (
    <div className="zemo-card p-6 space-y-5" data-testid="step-assinantes">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Assinantes do contrato</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            {cfg.copy.assinantesHint(MAX_SIGNERS)}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0"
          onClick={() => setRepDialog({ open: true, rep: null })}
          data-testid="signers-add-representative-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Cadastrar representante
        </Button>
      </div>

      {signersLoading && (
        <div className="flex items-center gap-2 text-sm text-zinc-500 py-4" data-testid="signers-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando representantes do cedente...
        </div>
      )}

      {!signersLoading && signersError && (
        <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800 space-y-1" data-testid="signers-error">
          <div>Não foi possível carregar os representantes: {signersError}</div>
          <button onClick={() => loadSigners({ preselect: true })} className="underline font-medium" data-testid="signers-retry-btn">
            Tentar novamente
          </button>
        </div>
      )}

      {!signersLoading && !signersError && signers !== null && signers.length === 0 && (
        <div className="rounded-md p-4 border border-amber-200 bg-amber-50 text-[13px] text-amber-800" data-testid="signers-empty">
          <div className="flex items-start gap-2">
            <UserRound className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Este cedente ainda não tem representante ativo cadastrado — e a
              operação PJ exige ao menos um assinante. Use{" "}
              <strong>Cadastrar representante</strong> acima (nome, CPF, e-mail,
              telefone e endereço completo) para continuar.
            </div>
          </div>
        </div>
      )}

      {!signersLoading && !signersError && (signers || []).length > 0 && (
        <>
          <div className="border border-[var(--zemo-border)] rounded-md divide-y divide-[var(--zemo-border)]">
            {signers.map((rep) => {
              const orderIdx = selectedSignerIds.indexOf(rep.id);
              const isSelected = orderIdx >= 0;
              const missing = signerMissingFields(rep);
              return (
                <div
                  key={rep.id}
                  className={`px-3 py-2.5 ${isSelected ? "bg-[#0055FF]/5" : ""}`}
                  data-testid={`signer-row-${rep.id}`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => toggleSigner(rep.id)}
                      aria-pressed={isSelected}
                      className={`h-6 w-6 mt-0.5 rounded-full border flex items-center justify-center text-[11px] font-semibold shrink-0 transition-colors
                            ${isSelected ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-[var(--zemo-border)] bg-white text-transparent hover:border-zinc-400"}
                          `}
                      data-testid={`signer-toggle-${rep.id}`}
                      title={isSelected ? "Remover dos assinantes" : "Adicionar aos assinantes"}
                    >
                      {isSelected ? orderIdx + 1 : "+"}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-zinc-900">{rep.full_name}</span>
                        {rep.is_default_signer && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#0055FF]/10 text-[#0055FF] border border-[#0055FF]/20">
                            Assinante padrão
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-zinc-500 font-mono-num">
                        {maskCPF(rep.document_number)} · {rep.email}
                      </div>
                      {missing.length > 0 && (
                        <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md px-2 py-1.5 border border-amber-200 bg-amber-50" data-testid={`signer-incomplete-${rep.id}`}>
                          <div className="flex items-start gap-1.5 text-[12px] text-amber-800">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>
                              {cfg.canEditRepresentative
                                ? `Falta para assinar: ${missing.join(", ")}.`
                                : REPRESENTANTE_INCOMPLETO_COPY}
                            </span>
                          </div>
                          {/* Edição inline exige PATCH — que NÃO existe no
                              ramo cedente (aguarda decisão do owner). Sem
                              ele, a saída é cadastrar um novo representante
                              com os dados completos. */}
                          {cfg.canEditRepresentative ? (
                            <button
                              type="button"
                              onClick={() => setRepDialog({ open: true, rep })}
                              className="text-[12px] font-medium text-amber-900 underline inline-flex items-center gap-1 shrink-0"
                              data-testid={`signer-complete-${rep.id}`}
                            >
                              <PenLine className="h-3 w-3" /> Completar cadastro
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between p-3 rounded-md bg-zinc-100 border border-[var(--zemo-border)]">
            <span className="text-sm text-zinc-600" data-testid="signers-count">
              {selectedSigners.length} de {MAX_SIGNERS} assinante(s) selecionado(s)
            </span>
            {selectedSigners.length >= MAX_SIGNERS && (
              <span className="text-[12px] text-zinc-500">Limite de {MAX_SIGNERS} atingido</span>
            )}
          </div>

          {selectedSigners.length === 0 && (
            <div role="alert" className="rounded-md p-3 border border-amber-200 bg-amber-50 text-[13px] text-amber-800" data-testid="signers-none-selected">
              Selecione ao menos um representante para assinar o contrato —
              sem assinante a operação não pode ser criada.
            </div>
          )}

          {incompleteSelected.length > 0 && (
            <div role="alert" className="rounded-md p-3 border border-amber-200 bg-amber-50 text-[13px] text-amber-800" data-testid="signers-incomplete-warning">
              Assinante selecionado com cadastro incompleto:{" "}
              {incompleteSelected.map((r) => `${r.full_name} (falta ${signerMissingFields(r).join(", ")})`).join("; ")}.
              {cfg.canEditRepresentative
                ? " Complete o cadastro (atalho na linha) ou remova da seleção para continuar."
                : ` ${REPRESENTANTE_INCOMPLETO_COPY} Remova da seleção para continuar.`}
            </div>
          )}
        </>
      )}

      <RepresentativeFormDialog
        open={repDialog.open}
        onOpenChange={(open) => setRepDialog((d) => ({ ...d, open }))}
        cedenteId={cedenteId}
        basePath={repBasePath}
        representative={repDialog.rep}
        onSaved={() => loadSigners(
          // Primeiro cadastro do cedente: pré-seleciona (o recém-criado
          // padrão entra); recarga de edição só poda seleção stale.
          (signers || []).length === 0 ? { preselect: true } : {}
        )}
      />
    </div>
  );
}
